/**
 * PhoneCountryCodeSelect Component
 *
 * Searchable phone country code selector.
 *
 * The countries are the caller's — `useCountries().countries` from
 * @tonnie/locale, named in the reader's language (F34). Flags are
 * `<CountryFlag>` images, never emoji (F32).
 */

import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../lib/utils';
import { CountryFlag } from './country-flag';
import type { CountryOption } from './country-select';
import { Button } from '../primitives/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../primitives/command';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';

export interface PhoneCountryCodeSelectProps {
  /** Every selectable country, translated and sorted — `useCountries().countries`. */
  countries: readonly CountryOption[];
  /** Selected country code (e.g., "US", "PT") */
  value?: string;
  /** Callback when country code is selected */
  onValueChange: (value: string) => void;
  /** Placeholder text when no code selected. Pass a translated string. */
  codePlaceholder?: string;
  /** Placeholder text for search input. Pass a translated string. */
  searchPlaceholder?: string;
  /** Message shown when no phone code matches search. Pass a translated string. */
  emptyMessage?: string;
  /** Additional CSS classes */
  className?: string;
  /**
   * The picker's accessible name — translated. Required: the trigger shows
   * only a flag and a dial code, which is no name for a screen reader.
   */
  'aria-label': string;
}

export function PhoneCountryCodeSelect({
  countries,
  value,
  onValueChange,
  codePlaceholder,
  searchPlaceholder,
  emptyMessage,
  className,
  'aria-label': ariaLabel,
}: PhoneCountryCodeSelectProps) {
  const [open, setOpen] = useState(false);

  const selectedCountry = value ? countries.find((c) => c.code === value.toUpperCase()) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn('w-[120px] justify-between h-10', className)}
        >
          {selectedCountry ? (
            <div className="flex items-center gap-1">
              <CountryFlag code={selectedCountry.code} size="sm" />
              <span className="text-sm">{selectedCountry.phoneCode}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{codePlaceholder}</span>
          )}
          <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.code} ${country.phoneCode}`}
                  onSelect={() => {
                    onValueChange(country.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === country.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <CountryFlag code={country.code} size="sm" className="mr-2" />
                  <span className="font-medium mr-2">{country.phoneCode}</span>
                  <span className="text-muted-foreground">{country.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
